import { Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CellClickedEvent, CellDoubleClickedEvent, GridOptions, GridReadyEvent } from 'ag-grid';
import { constant } from 'app/app.constants';
import { AgGridSortFilterUtils } from 'app/core/ag-grid-utils/ag-grid-sort-filter-utils';
import { AgButtonsEnum, DepositAction, DepositActions, DepositGridName, OptionImportEnum } from 'app/core/enums/tabs-deposits.enum';
import { FormatCurrencyHelper } from 'app/core/helpers/format-currency-helper';
import { CustomDate } from 'app/core/helpers/format-date-helper';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { IDeposit, ILockBox } from 'app/core/interfaces/deposit.interface';
import { ConciliationService } from 'app/core/services/conciliation.service';
import { ParentModule } from 'app/pages/A2-utils/ParentClass/parent-module';
import { UploadsService } from 'app/pages/A2-utils/uploads.service';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { Subscription } from 'rxjs';
import { getCellStylesByStatus } from '../helper';

@Component({
  selector: 'app-lockbox',
  templateUrl: './lockbox.component.html',
  styleUrls: ['./lockbox.component.scss']
})
export class LockboxComponent extends ParentModule implements OnInit, OnDestroy {
  @ViewChild('modalLockBox') modalLockBox: T2CardComponent;
  @ViewChild('modalImportLockBox') modalImportLockBox: T2CardComponent;
  @ViewChild('fileLockBox') fileLockBox: ElementRef;
  gridLockBoxOptions: GridOptions;
  subscriptions: Subscription[] = [];
  lockBoxCrudForm: FormGroup;
  lockBoxSearch: FormGroup;
  reqSearhc: IDeposit = { depositdate: null };
  totalRows = 0;
  optionImportLockBox = OptionImportEnum.LockBox;
  agGridButtonActionList = {
    RemoveFilter: AgButtonsEnum.RemoveFilter,
    RemoveSort: AgButtonsEnum.RemoveSort
  };
  enableClearFilter = true;
  enableClearSort = true;
  totalAmount = 0;
  totalLockBoxAmountSelected = 0;
  selectedRowIndex = null;
  public actionsDeposit = [];

  get sequenceVal(): number {
    return this.lockBoxCrudForm.get('sequence').value;
  }

  get amountpaidVal(): number {
    return this.lockBoxCrudForm.get('amountpaid').value;
  }

  get depositMonthLckBoxCtrl(): AbstractControl {
    return this.lockBoxSearch.get('depositdatelockbox');
  }

  get filesNamesCtrl(): AbstractControl {
    return this.lockBoxSearch.get('file');
  }

  get uploadfilesCtrl(): AbstractControl {
    return this.lockBoxSearch.get('uploadfiles');
  }

  get showFileInputLockBox(): string {
    return '.xls, .xlsx, .csv';
  }

  noBlankSpace = constant.NO_BLANK_SPACE;

  constructor(
    private fb: FormBuilder,
    private t2LoadingService: T2LoadingService,
    private sweetAlertHelper: SweetAlertHelper,
    protected objServ: ConciliationService,
    private objToast: ToastrAlertHelper,
    private uploadsService: UploadsService,
    protected inject: Injector
  ) {
    super('app-deposits', inject);
    this.createForm();
    this.gridBuilder();
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getDepositbyCurrentMonth();
  }

  getDepositbyCurrentMonth(): void {
    this.lockBoxSearch.get('depositdatelockbox')
    .patchValue(CustomDate.formatJsV2(CustomDate.getDate()), { emitEvent: false });
  }

  createForm(): void {
    this.lockBoxSearch = this.fb.group({
      depositdatelockbox: ['', Validators.required],
      file: '',
      uploadfiles: ''
    });
    this.lockBoxCrudForm = this.fb.group({
      lockboxid: null,
      trandate: null,
      checkdate: [null, Validators.required],
      checkaccount: null,
      sequence: null,
      serialnumber: [null, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      amountpaid: [null, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      payor: [null, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      posted: [null, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      note: '',
      statusid: [null, Validators.required],
      statusdescription: [null, Validators.required],
      reviewedby: null,
      reviewedfullname: null
    });
  }

  gridBuilder(): void {
    this.gridLockBoxOptions = <GridOptions> {
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      suppressRowClickSelection: true,
      columnDefs: [
        {
          headerName: 'PO BOX',
          field: 'pobox',
          width: 100,
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'Lockbox ID',
          field: 'lockboxid',
          cellStyle: { 'text-align': 'end' },
          hide: true
        },
        {
          headerName: 'Tran Date',
          field: 'trandate',
          width: 110,
          valueGetter: (params) => {
            if (params && params.data.trandate) {
              return CustomDate.formatJs(params.data.trandate);
            }
          },
          cellStyle: { 'text-align': 'center' }
        },
        {
          headerName: 'Location',
          field: 'location',
          width: 150
        },
        {
          headerName: 'Sequence',
          field: 'sequence',
          cellStyle: { 'text-align': 'end' },
          width: 100
        },
        {
          headerName: 'Serial Number',
          field: 'serialnumber',
          width: 150
        },
        {
          headerName: 'Amount Paid',
          field: 'amountpaid',
          cellStyle: { 'text-align': 'end' },
          width: 120,
          cellRenderer: (params) => {
            const decimal = 0;
            return params && params.data.amountpaid
            ? '$' + params.data.amountpaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '$' + decimal.toFixed(2);
          },
          filterParams: AgGridSortFilterUtils.sortFilterColumn
        },
        {
          headerName: 'Posted',
          field: 'posted',
          cellStyle: { 'text-align': 'end' },
          width: 120,
          cellRenderer: (params) => {
            const decimal = 0;
            return params && params.data.posted
            ? '$' + params.data.posted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '$' + decimal.toFixed(2);
          },
          filterParams: AgGridSortFilterUtils.sortFilterColumn
        },
        {
          headerName: 'Payor',
          field: 'payor',
          cellStyle: { 'text-align': 'start' }
        },
        {
          headerName: 'StatusId',
          field: 'statusid',
          width: 120,
          hide: true
        },
        {
          headerName: 'Deposit Status',
          field: 'depositstatusdescription',
          width: 120,
          cellStyle: (params) => getCellStylesByStatus().stylesByDeposit(params.data.depositstatusid)
        },
        {
          headerName: 'Posted Status',
          field: 'postedstatusdescription',
          width: 120,
          cellStyle: (params) => getCellStylesByStatus().stylesByPosted(params.data.postedstatusid)
        },
        {
          headerName: 'Deposit Id',
          field: 'depositid',
          cellStyle: { 'text-align': 'start' }
        },
        {
          headerName: 'Posted Id',
          field: 'postedid',
          cellStyle: { 'text-align': 'start' }
        },
        {
          headerName: 'Reviewed by ID',
          field: 'reviewedby',
          cellStyle: { 'text-align': 'start' },
          hide: true
        },
        {
          headerName: 'Reviewed by',
          field: 'reviewedfullname',
          cellStyle: { 'text-align': 'start' }
        },
        {
          headerName: 'Note',
          field: 'note',
          cellStyle: { 'text-align': 'start' }
        },
        {
          headerName: 'Imported On',
          field: 'importedon',
          width: 160,
          hide: true,
          valueGetter: (params) => {
            if (params && params.data && params.data.importedon) {
              return CustomDate.formatJs(params.data.importedon);
            }
          },
          cellRenderer: (params) => {
            let ico = '';
            let wasImported = null;

            if (params && params.data && params.data.importedon) {
              ico = 'fa icofont-check color-success';
              wasImported = 'Imported';
            }

            return wasImported &&
            `<center>${params.data.importedon}
            <i class="${ico}" aria-hidden="true" style='font-size: 20px' title="${wasImported}"></i></center>`;
          }
        },
        {
          headerName: 'Check Date',
          field: 'checkdate',
          width: 110,
          valueGetter: (params) => {
            if (params && params.data.checkdate) {
              return CustomDate.formatJs(params.data.checkdate);
            }
          },
          cellStyle: { 'text-align': 'center' }
        }
      ],
      onGridReady: (event: GridReadyEvent) => {
        if (event) {
          this.gridLockBoxOptions.api.setRowData([]);
          this.gridLockBoxOptions.api.sizeColumnsToFit();
        }
      },
      onCellClicked: (event: CellClickedEvent) => {
        if (event && event.api) {
          event.api.clearFocusedCell();
        }
      },
      onRowClicked: (event) => {
        this.selectedRowIndex = event.data.lockboxid;
        this.gridLockBoxOptions.api.redrawRows();
      },
      onCellDoubleClicked: (params: CellDoubleClickedEvent) => {
        if (params && params.data && params.data.lockboxid) {
          this.modalLockBox.open();
          this.setterData(params);
        }
      },
      onFilterChanged: () => {
        this.selectedRowIndex = null;
        this.modalLockBox.close();
        this.updateButtonState();
        this.gridLockBoxOptions.api.redrawRows();
      },
      onSortChanged: () => {
        this.modalLockBox.close();
        this.updateButtonState();
      },
      rowClassRules: {
        'row-default-deposit-grid': (params) => {
          return params.data.lockboxid !== null;
        },
        'row-selected-deposit': (params) => {
          return params && params.data && params.data.lockboxid === this.selectedRowIndex;
        }
      },
      getRowNodeId: (data) => data.lockboxid
    };
  }

  initSubscriptions(): void {
    this.subscriptions.push(
      this.objServ.behaviorSubjDepositObs$.subscribe({
        next: (res) => {
          if (res && res.action === DepositAction.SearchLogBox && res.data) {
            const dateMonthLockBox = this.depositMonthLckBoxCtrl.value;
            const req: IDeposit = { depositdate: CustomDate.formatJs(dateMonthLockBox, 'MM-yyyy') };
            this.searchPosted(req, true);
            this.modalLockBox.close();
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      }),
      this.objServ.behaviorSubjDeposit.subscribe({
        next: (res) => {
          if (res && res.data &&
            (
              res.action === DepositAction.MonthPickerSetterValueChangesDeposit ||
              res.action === DepositAction.MonthPickerSetterValueChangesPostedResp ||
              res.action === DepositAction.MonthPickerSetterValueChangesConciliationResp ||
              res.action ===  DepositAction.MonthPickerSetterValueChangesElectronicResp
            )
          ) {
            this.depositMonthLckBoxCtrl.patchValue(res.data, { emitEvent: false });
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      }),
      this.lockBoxSearch.get('depositdatelockbox')
      .valueChanges.subscribe({
        next: (res) => {
          if (res) {
            this.objServ.behaviorSubjDeposit.next({
              action: DepositAction.MonthPickerSetterValueChangesLockBoxResp,
              data: res
            });
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      }),
      this.$actions.subscribe({
        next: (value) => {
          if (value && value.length) {
            this.actionsDeposit = value;
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      })
    );
  }

  searchPosted(req: IDeposit, fitColumns = false): void {
    this.selectedRowIndex = null;
    if (req.depositdate) {
      const filterModel = this.gridLockBoxOptions.api.getFilterModel();
      const fortModel = this.gridLockBoxOptions.api.getSortModel();

      this.t2LoadingService.show();
      this.subscriptions.push(
        this.objServ.getLockBox(req)
        .finally(() => this.t2LoadingService.hiden())
        .subscribe({
          next: (resp) => {
            if (resp && resp.length) {
              this.totalRows = resp.length;
              this.gridLockBoxOptions.api.setRowData(resp);
              this.getTotalLockBoxGrid(DepositGridName.LockBoxGrid, resp);
              this.gridLockBoxOptions.api.setFilterModel(filterModel);
              this.gridLockBoxOptions.api.setSortModel(fortModel);
              if (fitColumns) {
                this.gridLockBoxOptions.api.sizeColumnsToFit();
              }
            } else {
              this.clearCountAndGrid();
            }
          },
          error: (er) => this.sweetAlertHelper.captureException(er)
        })
      );
    } else {
      this.clearCountAndGrid();
    }
  }

  getCtrlByName(controlName: string): AbstractControl {
    return this.lockBoxCrudForm.get(controlName);
  }

  setterData(params: any): void {
    if (params && params.data && params.data.lockboxid) {
      this.getCtrlByName('lockboxid').patchValue(params.data.lockboxid);
      this.getCtrlByName('trandate').patchValue(params.data.trandate);
      this.getCtrlByName('checkaccount').patchValue(params.data.checkaccount);
      this.getCtrlByName('sequence').patchValue(params.data.sequence);
      this.getCtrlByName('serialnumber').patchValue(params.data.serialnumber);
      this.getCtrlByName('amountpaid').patchValue(params.data.amountpaid);
      this.getCtrlByName('statusid').patchValue(params.data.statusid);
      this.getCtrlByName('statusdescription').patchValue(params.data.statusdescription);
      this.getCtrlByName('note').patchValue(params.data.note);
      this.getCtrlByName('posted').patchValue(params.data.posted);
      this.getCtrlByName('payor').patchValue(params.data.payor);
      this.getCtrlByName('reviewedby').patchValue(params.data.reviewedby);
      this.getCtrlByName('reviewedfullname').patchValue(params.data.reviewedfullname);
      const checkDateI = CustomDate.formatJs(params.data.checkdate, 'yyyy-MM-dd');
      this.getCtrlByName('checkdate').patchValue(checkDateI);
    }
  }

  formatCurrency(value: string): string {
    const formatVal = isNaN(Number(value)) ? 0 : Number(value);
    return FormatCurrencyHelper.formatCurrency(formatVal);
  }

  clearControl(controlName: string, value = ''): void {
    this.lockBoxCrudForm.get(controlName).patchValue(value);
  }

  resetForm(): void {
    this.lockBoxCrudForm.reset({
      payor: null
    });
  }

  saveChanges(): void {
    if (this.lockBoxCrudForm.valid) {
      this.t2LoadingService.show();
      const formData = this.lockBoxCrudForm.getRawValue();
      if (formData.lockboxid) {
        const req: ILockBox = {
          lockboxid: formData.lockboxid,
          serialnumber: formData.serialnumber,
          posted: formData.posted,
          payor: formData.payor,
          note: formData.note ? formData.note : '',
          checkdate: formData.checkdate
        };

        this.subscriptions.push(
          this.objServ.updateLockBox(req)
          .finally(() => this.t2LoadingService.hiden())
          .subscribe({
            next: (resp: any) => {
              if (resp && resp.message) {
                this.objToast.success(resp.message, '', { positionClass: 'toast-bottom-right' });

                if (resp.data) {
                  this.getCtrlByName('reviewedby').patchValue(resp.data.reviewedby);
                  this.getCtrlByName('reviewedfullname').patchValue(resp.data.reviewedfullname);
                  this.getCtrlByName('checkdate').patchValue(resp.data.checkdate);
                  this.updateGridCell(resp.data);
                }
                this.modalLockBox.close();
              }
            },
            error: (er) => this.sweetAlertHelper.captureException(er)
          })
        );
      }
    } else {
      this.objToast.error('Action not allowed', '', { positionClass: 'toast-bottom-right' });
    }
  }

  updateGridCell(data: any) {
    const rowNode = this.gridLockBoxOptions.api.getRowNode(data.lockboxid);
    rowNode.setData(data);
  }

  run(): void {
    this.modalLockBox.close();
    if (this.validRunAction()) {
      const dateMonthLockBox = this.depositMonthLckBoxCtrl.value;
      const req: IDeposit = { depositdate: CustomDate.formatJs(dateMonthLockBox, 'MM-yyyy') };
      this.searchPosted(req);
    } else {
      this.clearCountAndGrid();
      this.objToast.error('Action not allowed', '', { positionClass: 'toast-bottom-right' });
    }
  }

  clearMonthDate(): void {
    this.depositMonthLckBoxCtrl.patchValue('', { emitEvent: false });
  }

  buildRequest(dataSource: IDeposit): IDeposit {
    let req: IDeposit = null;
    if (dataSource.depositdate) {
      req  = { depositdate: CustomDate.formatJs(dataSource.depositdate, 'MM-yyyy') };
    }
    return req;
  }

  openModalByName(): void {
    this.modalImportLockBox.open();
  }

  selectedFiles(event: any): void {
    if (event && event.target && event.target.files && event.target.files.length) {
      let text = '';
      for (const name of event.target.files) {
        text += name.name + ', ';
      }
      this.lockBoxSearch.patchValue({
        file: text,
        uploadfiles: event.target.files,
      });
    }
  }

  resetImportFile(): void {
    this.fileLockBox.nativeElement.value = '';
    this.lockBoxSearch.patchValue({ file: '', uploadfiles: ''});
  }

  closeModalLockBox(): void {
    this.resetImportFile();
    this.modalImportLockBox.close();
  }

  uploadFile(): void {
    if (this.fileSelected()) {
      this.sweetAlertHelper
      .createCustomAlert({
        title: 'Process File',
        text: 'Are you sure you want to process these deposits files?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, import it!',
        cancelButtonText: 'No, cancel!',
      })
      .then((result) => {
        if (result.value) {
          this.t2LoadingService.show();
          const uploadFiles = this.uploadfilesCtrl.value;
          const params = [{ nameParam: 'dataBodySpreadSheet', data: { extension: 'xls' } }];
          this.subscriptions.push(
            this.uploadsService
            .makeFileRequest(`${constant.URL_API_DEPOSIT_UPLOAD_FILE}?optionImport=${this.optionImportLockBox}`,
              params, uploadFiles, 'file')
            .finally(() => this.t2LoadingService.hiden())
            .subscribe({
              next: (resp) => {
                if (resp && (resp.totalinserted || resp.totalduplicates || resp.totaltoinsert)) {
                  this.sweetAlertHelper.createCustomAlert({
                    title: 'Import File',
                    text: `Lock Box Import Success`,
                    type: 'success',
                    html: `<div>Total Inserted: ${resp.totalinserted ? resp.totalinserted : 0 }</div>
                          <div>Total Duplicates: ${resp.totalduplicates ? resp.totalduplicates : 0}</div>
                          <div>Total: ${resp.totaltoinsert ? resp.totaltoinsert : 0}</div>`
                  }).then(() => {
                    this.t2LoadingService.hiden();
                    this.closeModalLockBox();
                  });
                }
              },
              error: (er) => {
                this.sweetAlertHelper.captureException(er)
                .then(() => this.closeModalLockBox());
              }
            })
          );
        }
      });
    } else {
      this.objToast.error('Action not allowed', '', { positionClass: 'toast-bottom-right' });
      this.closeModalLockBox();
    }
  }

  getTotalLockBoxGrid(objTable: string, dataSource: any[]): void {
    switch (objTable) {
      case DepositGridName.LockBoxGrid:
        if (dataSource && dataSource.length) {
          this.totalAmount = dataSource.reduce((sum, item) => sum + item.amountpaid, 0);
          this.totalLockBoxAmountSelected = dataSource.reduce((sum, item) => sum + item.amountpaid, 0);
        }
        break;
      default:
        break;
    }
  }

  clearFilterAndSort(nameBtn: AgButtonsEnum): void {
    switch (nameBtn) {
      case AgButtonsEnum.RemoveFilter:
        this.totalLockBoxAmountSelected = this.totalAmount;
        this.gridLockBoxOptions.api.setFilterModel(null);
        break;
      case AgButtonsEnum.RemoveSort:
        this.gridLockBoxOptions.api.setSortModel(null);
        break;
      default:
        break;
    }
    this.updateButtonState();
  }

  fileSelected(): boolean {
    return this.filesNamesCtrl.value !== '';
  }

  updateButtonState(): void {
    const filterApply = !Object.keys(this.gridLockBoxOptions.api.getFilterModel()).length;
    this.enableClearFilter = filterApply;
    this.enableClearSort = !this.gridLockBoxOptions.api.getSortModel().length;

    if (!filterApply) {
      this.sumTotalLockBoxSelected();
    }
  }

  sumTotalLockBoxSelected(): void {
    let total = 0;
    this.gridLockBoxOptions.api.forEachNodeAfterFilterAndSort((node) => {
      if (node && node.data && node.data.amountpaid) {
        total += node.data.amountpaid;
      }
    });
    this.totalLockBoxAmountSelected = total;
  }

  formatCurrencyText(valueFormated): string {
    const formatVal = isNaN(Number(valueFormated)) ? 0 : Number(valueFormated);
    return FormatCurrencyHelper.formatCurrency(formatVal);
  }

  validRunAction(): boolean {
    const depositdate = this.depositMonthLckBoxCtrl.value;
    return depositdate;
  }

  clearCountAndGrid(): void {
    this.totalLockBoxAmountSelected = 0;
    this.totalRows = 0;
    if (this.gridLockBoxOptions && this.gridLockBoxOptions.api) {
      this.gridLockBoxOptions.api.setRowData([]);
    }
  }

  execSpUpdateLockBox(): void {
    if (this.actionsDeposit.includes(DepositActions.PostedUpdateLockBoxStatus)) {
      this.t2LoadingService.show();
      this.subscriptions.push(
        this.objServ.updateLockBoxStatus(this.depositMonthLckBoxCtrl.value)
        .finally(() => this.t2LoadingService.hiden())
        .subscribe({
          next: (res) => {
            if (res && res.message) {
              this.objToast.success(res.message, 'Updated LockBox Status',
                { positionClass: 'toast-bottom-right' });
              this.run();
            }
          },
          error: (err) => this.sweetAlertHelper.captureException(err)
        })
      );
    } else {
      this.objToast.error('You do not have permission to perform this action', 'Access Denied!',
        { positionClass: 'toast-bottom-right' });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/deposits/lockbox/lockbox.component.ts